<script lang="ts">
  import type { ManageAPI } from "~/manage-api/types";
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import { currentOfferId, currentOfferDetails, currentRequestStoreKey, closeOfferModal } from "../../stores/offerModal.ts";
  import { formatDateRange } from "../common/date-utils";
  
  export let request_store_key: StoresKey;
  
  // Badge styling props
  export let badgeBackgroundColor: string = "";
  export let badgeTextColor: string = "";
  export let badgeBorderRadius: string = "";
  export let badgeFontSize: string = "";
  
  // Modal styling props
  export let modalBorderRadius: string = "";
  export let modalBackgroundColor: string = "";
  export let modalTextColor: string = "";
  export let modalPaddingVertical: string = "";
  export let modalPaddingHorizontal: string = "";
  export let modalMaxWidth: string = "";
  export let modalMaxHeight: string = "";
  export let modalCloseButtonBorderRadius: string = "";
  
  // Use the global store to track offer ID and details
  $: modalId = $currentOfferId;
  $: offer = $currentOfferDetails;
  $: actualRequestStoreKey = $currentRequestStoreKey || request_store_key;
  
  // Badge styling - use custom props if provided
  $: badgeStyle = "";
  $: {
    const styleProps = [];
    if (badgeBackgroundColor) styleProps.push(`--offer-modal-badge-bg-color: ${badgeBackgroundColor}`);
    if (badgeTextColor) styleProps.push(`--offer-modal-badge-text-color: ${badgeTextColor}`);
    if (badgeBorderRadius) styleProps.push(`--offer-modal-badge-border-radius: ${badgeBorderRadius}`);
    if (badgeFontSize) styleProps.push(`--offer-modal-badge-font-size: ${badgeFontSize}`);
    
    badgeStyle = styleProps.join('; ');
  }
  
  // Modal styling - use custom props if provided
  $: modalStyle = "";
  $: {
    const styleProps = [];
    if (modalBorderRadius) styleProps.push(`--offer-modal-border-radius: ${modalBorderRadius}`);
    if (modalBackgroundColor) styleProps.push(`--offer-modal-background-color: ${modalBackgroundColor}`);
    if (modalTextColor) styleProps.push(`--offer-modal-text-color: ${modalTextColor}`);
    if (modalPaddingVertical) styleProps.push(`--offer-modal-padding-vertical: ${modalPaddingVertical}`);
    if (modalPaddingHorizontal) styleProps.push(`--offer-modal-padding-horizontal: ${modalPaddingHorizontal}`);
    if (modalMaxWidth) styleProps.push(`--offer-modal-max-width: ${modalMaxWidth}`);
    if (modalMaxHeight) styleProps.push(`--offer-modal-max-height: ${modalMaxHeight}`);
    if (modalCloseButtonBorderRadius) styleProps.push(`--offer-modal-close-button-border-radius: ${modalCloseButtonBorderRadius}`);
    
    modalStyle = styleProps.join('; ');
  }
  
  // Modal handling
  let previouslyFocused: Element | null = null;
  let modalBackdrop: HTMLElement;

  // When modalId changes and the modal should open, save previously focused element
  $: if (modalId) {
    previouslyFocused = document.activeElement;
    setTimeout(() => {
      if (modalBackdrop) {
        modalBackdrop.focus();
      }
    }, 0);
  }

  function closeModal(e?: Event) {
    if (e) {
      e.preventDefault();
    }
    
    // Use the store function to properly close the modal
    closeOfferModal();
    
    if (previouslyFocused && previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      closeModal();
    }
  }
  
  // Store context and content variables
  let store: ManageAPI.Store | undefined;
  let store_href = "";
  let logo = "";
  let alt = "";
  
  // Update store info when offer or context changes
  $: if (offer && actualRequestStoreKey) {
    getStoreInfo();
  }
  
  function getStoreInfo() {
    if (!offer) return;
    
    const storesContext = getStoresContext(actualRequestStoreKey);
    const { reverse_router, stores, scdata, site } = storesContext;
    
    store = stores.value && stores.value.find((x: ManageAPI.Store) => x.id == offer.store?.id);
    store_href = store && reverse_router.value && scdata.value ? reverse_router.value.routeStore(store, scdata.value) : "";
    logo = store?.logoURL || "";
    alt = store?.name || site.value?.name || "";
  }
</script>

{#if modalId && offer}
  <div 
    class="modal-backdrop"
    role="button"
    on:click={closeModal}
    on:keydown={handleKeydown}
    tabindex="0"
    bind:this={modalBackdrop}
  >
    <div 
      class="modal-wrapper" 
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="presentation"
      style={modalStyle}
    >
      <div class="modal-content offer-dialog" role="dialog" aria-modal="true">
        <div class="dialog-content" role="document">
          <!-- Offer Content -->
          <div class="square cut-bottom-left mb-3">
            {#if offer.images && offer.images.length}
              <img src={offer.images.at(0)} {alt} />
            {:else}
              <img src={logo} {alt} />
            {/if}
          </div>
          <div class="offer-modal-details">
            <div class="offer-detail"> 
              <div class="offer-meta">
                <p style="font-size: 14px;">
                  {formatDateRange(offer.validityStartDate, offer.endDate)}
                </p>
                {#if store}
                  <a class="publisher text-gray" href={store_href} style={badgeStyle}>{alt}</a>
                {:else}
                  <span class="publisher text-gray" style={badgeStyle}>{alt}</span>
                {/if}
              </div>

              <h2 class="description">
                {offer.description1}
                {offer.description2}
              </h2>
              {#if offer.longDescription}
                <p class="longDescription">{offer.longDescription}</p>
              {/if}
            </div>
          </div>
        </div>
      </div>
      <button 
        type="button" 
        class="close-button" 
        on:click={closeModal}
        aria-label="Close dialog"
      >
        <span class="close-icon"></span>
      </button>
    </div>
  </div>
{/if}

<style lang="scss">
  /* Modal Styles */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    
    @media screen and (max-width: 767px) {
      align-items: flex-start;
      padding-top: 80px; /* Offset from top of viewport */
    }
  }

  .modal-wrapper {
    position: relative;
    width: auto;
  }

  .modal-content {
    background: var(--offer-modal-background-color, white);
    border-radius: var(--offer-modal-border-radius, 24px);
    padding: 0;
    max-height: var(--offer-modal-max-height, 90vh);
    width: 100%;
    color: var(--offer-modal-text-color, #000);
    &:focus {
      outline: none;
    }
    
    @media screen and (max-width: 767px) {
      max-height: 80vh; /* Slightly shorter to fit in viewport */
    }
  }

  .dialog-content {
    padding: var(--offer-modal-padding-vertical, 40px) var(--offer-modal-padding-horizontal, 40px);
    max-height: calc(var(--offer-modal-max-height, 90vh) - 20px); /* Account for padding */
    overflow: auto;
    max-width: var(--offer-modal-max-width, 500px);
    margin: 0 auto;
    
    @media screen and (max-width: 768px) {
      padding: calc(var(--offer-modal-padding-vertical, 40px) / 2) calc(var(--offer-modal-padding-horizontal, 40px) / 2);
      max-width: 100%;
    }
  }

  /* Close button styles */
  .close-button {
    position: absolute;
    top: 0;
    right: -60px;
    background: var(--offer-modal-background-color, white);
    border: none;
    border-radius: var(--offer-modal-close-button-border-radius, 8px);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);

    &:hover {
      background: #f5f5f5;
      
      .close-icon::before,
      .close-icon::after {
        background-color: var(--offer-modal-text-color, #000);
      }
    }

    @media screen and (max-width: 767px) {
      position: absolute;
      top: -50px;
      right: 10px;
      z-index: 1001;
    }
  }
  
  .close-icon {
    position: relative;
    width: 16px;
    height: 16px;
    
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 2px;
      background-color: var(--offer-modal-text-color, #000);
      transition: background-color 0.2s;
    }
    
    &::before {
      transform: rotate(45deg);
    }
    
    &::after {
      transform: rotate(-45deg);
    }
  }

  /* Offer Content Styles */
  .offer-meta {
    font-family: var(--liferay-font-family-base);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    color: var(--offer-modal-text-color, #000);
  }

  .offer-detail {
    max-height: calc(var(--offer-modal-max-height, 90vh) * 0.3);
    overflow: auto;
    @media screen and (min-width: 768px) {
      max-height: calc(var(--offer-modal-max-height, 90vh) * 0.25);
    }
    @media screen and (max-width: 767px) {
      max-height: calc(var(--offer-modal-max-height, 90vh) * 0.35);
      padding-bottom: 10px;
    }
  }

  .description {
    margin-top: 1rem;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 2%;
    color: var(--offer-modal-text-color, #000);
    @media screen and (max-width: 475px) {
      font-size: 14px;
    }
  }
  .longDescription {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: var(--offer-modal-text-color, #000);
    @media screen and (max-width: 475px) {
      font-size: 14px;
    }
  }

  .offer-modal-details {
    width: 100%;
  }

  .publisher {
    font-size: var(--offer-modal-badge-font-size, 16px);
    font-weight: 600;
    text-decoration: none;
    text-transform: uppercase;
    background-color: var(--offer-modal-badge-bg-color, #f5f5f5);
    color: var(--offer-modal-badge-text-color, inherit);
    padding: 5px 10px;
    border-radius: var(--offer-modal-badge-border-radius, 4px);
    -webkit-appearance: none;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
    
    @media screen and (max-width: 475px) {
      font-size: calc(var(--offer-modal-badge-font-size, 16px) - 2px);
    }
  }

  a.publisher:hover {
    color: var(--offer-modal-badge-text-color, inherit);
    text-decoration: none;
  }

  .square {
    position: relative;
    background: var(--offer-modal-background-color, #fff);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .square img {
    max-width: 100%;
    width: 450px;
    height: 450px;
    max-height: 100%;
    border-radius: var(--offer-modal-border-radius, 10px);
    object-fit: cover;

    @media screen and (max-width: 768px) {
      width: 100%;
      height: 350px;
    }
    
    @media screen and (max-width: 480px) {
      height: 250px;
    }
  }
</style> 